import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import Footer from "../Footer/Footer";
import Navbar from "../Navbar/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import swal from "sweetalert";
import config from "../../Config";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import CommonShareDialog from "../CommonShareable/CommonShareDialog";

const ProductCard = ({ item, onClick }) => {
  const image =
    Array.isArray(item?.productImage) && item.productImage.length > 0
      ? item.productImage[0]
      : "";

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 0,
        overflow: "hidden",
        background: "#fff",
        border: "1px solid #ddd7ce",
        cursor: "pointer",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 18px 45px rgba(23,23,23,0.10)",
        },
        "&:hover .owner-product-image": {
          transform: "scale(1.045)",
        },
        "&:hover .owner-product-arrow": {
          background: "#171717",
          color: "#fff",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          background: "#e9e4dc",
        }}
      >
        {image ? (
          <CardMedia
            component="img"
            image={image}
            alt={item?.title || "Wardrobe piece"}
            className="owner-product-image"
            sx={{
              width: "100%",
              height: {
                xs: 330,
                sm: 350,
                md: 390,
              },
              objectFit: "cover",
              transition: "transform 0.65s ease",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: {
                xs: 330,
                sm: 350,
                md: 390,
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#e9e4dc",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: 44,
                color: "#968e83",
              }}
            >
              HW
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            position: "absolute",
            top: 15,
            left: 15,
            px: 1.4,
            py: 0.75,
            background: "rgba(247,244,239,0.94)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography
            sx={{
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#171717",
            }}
          >
            Available for Rent
          </Typography>
        </Box>

        <IconButton
          className="owner-product-arrow"
          sx={{
            position: "absolute",
            right: 15,
            bottom: 15,
            width: 40,
            height: 40,
            borderRadius: 0,
            background: "rgba(247,244,239,0.94)",
            color: "#171717",
            transition: "all 0.3s ease",
            "&:hover": {
              background: "#171717",
            },
          }}
        >
          <ArrowForwardIosRoundedIcon
            sx={{
              fontSize: 13,
            }}
          />
        </IconButton>
      </Box>

      <CardContent
        sx={{
          p: {
            xs: 2,
            md: 2.5,
          },
          "&:last-child": {
            pb: {
              xs: 2,
              md: 2.5,
            },
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Georgia, serif",
            fontSize: {
              xs: 21,
              md: 23,
            },
            fontWeight: 400,
            lineHeight: 1.2,
            textTransform: "capitalize",
            mb: 1.2,
            color: "#171717",
          }}
        >
          {item?.title || "Wardrobe Piece"}
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={0.7}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            ₹{Number(item?.rentAmount || 0).toLocaleString("en-IN")}
          </Typography>

          <Typography
            sx={{
              fontSize: 10,
              color: "#77716a",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            / day
          </Typography>
        </Stack>

        <Divider
          sx={{
            my: 2,
            borderColor: "#eee9e2",
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#977743",
              flexShrink: 0,
            }}
          >
            Available
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: "#77716a",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {item?.availableDate || "Availability on request"}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function OwnerProfile() {
  const [shareOpen, setShareOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [productList, setProductList] = useState([]);
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  const { ownerid } = useParams();
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("userId");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${config.apiBaseUrl}/${config.ownerProfile}`,
          {
            headers: {
              "owner-id": ownerid,
              ...(userId && {
                "user-id": userId,
              }),
            },
          },
        );

        const data = response?.data || {};

        setUserData(data?.userData || null);

        setProductList(
          Array.isArray(data?.productList) ? data.productList : [],
        );

        setFollowers(Number(data?.followerCount || 0));

        setFollowing(Number(data?.followingCount || 0));

        setIsFollowing(Boolean(data?.isFollowing));
      } catch (error) {
        console.error("Error fetching owner profile:", error);

        setUserData(null);
        setProductList([]);
      } finally {
        setLoading(false);
      }
    };

    if (ownerid) {
      fetchData();
    }
  }, [ownerid, userId]);

  const handleFollowToggle = async () => {
    if (!userId) {
      swal("Please login first", "", "warning");
      return;
    }

    try {
      const url = isFollowing
        ? "https://backend.hindustanwardrobe.in/user/unFollowOwner"
        : "https://backend.hindustanwardrobe.in/user/followOwner";

      await axios.post(
        url,
        {},
        {
          headers: {
            "owner-id": ownerid,
            "user-id": userId,
          },
        },
      );

      setIsFollowing((previous) => !previous);

      setFollowers((previous) =>
        Math.max(0, previous + (isFollowing ? -1 : 1)),
      );
    } catch (error) {
      swal("Something went wrong", "", "error");
    }
  };

  const handleProductClick = (productId) => {
    if (productId) {
      navigate(`/productdetail/${productId}`);
    }
  };

  const ownerName = userData?.name || "Wardrobe Partner";

  const username = ownerName.toLowerCase().replace(/\s+/g, "");

  const profileShareUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const profileDescription =
    "Discover curated wardrobe pieces available for rental on Hindustan Wardrobe.";

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "100vh",
          background: "#f7f4ef",
          color: "#171717",
        }}
      >
        {loading ? (
          <Box
            sx={{
              minHeight: "75vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack alignItems="center" spacing={2}>
              <PropagateLoader color="#977743" size={8} />

              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 18,
                  color: "#77716a",
                }}
              >
                Opening wardrobe...
              </Typography>
            </Stack>
          </Box>
        ) : !userData ? (
          <Box
            sx={{
              minHeight: "70vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
            }}
          >
            <Stack alignItems="center" spacing={2}>
              <Typography
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "#977743",
                }}
              >
                Profile Update
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: {
                    xs: 36,
                    md: 48,
                  },
                  fontWeight: 400,
                }}
              >
                Profile unavailable
              </Typography>

              <Typography
                sx={{
                  maxWidth: 450,
                  color: "#77716a",
                  fontSize: 13,
                  lineHeight: 1.8,
                }}
              >
                This wardrobe partner could not be found at the moment.
              </Typography>

              <Button
                onClick={() => navigate("/")}
                sx={{
                  mt: 1,
                  minHeight: 50,
                  px: 4,
                  borderRadius: 0,
                  background: "#171717",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  "&:hover": {
                    background: "#977743",
                  },
                }}
              >
                Explore Wardrobe
              </Button>
            </Stack>
          </Box>
        ) : (
          <Container
            maxWidth="xl"
            sx={{
              pt: {
                xs: 3,
                sm: 4,
                md: 6,
              },
              pb: {
                xs: 6,
                md: 10,
              },
            }}
          >
            <Box
              sx={{
                maxWidth: 1180,
                mx: "auto",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "170px 1fr auto",
                  },
                  alignItems: "center",
                  gap: {
                    xs: 3,
                    md: 5,
                  },
                  background: "#fff",
                  border: "1px solid #ddd7ce",
                  px: {
                    xs: 2.5,
                    sm: 4,
                    md: 5,
                  },
                  py: {
                    xs: 3,
                    md: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      md: "flex-start",
                    },
                  }}
                >
                  <Avatar
                    src={userData?.profilePic || ""}
                    sx={{
                      width: {
                        xs: 105,
                        md: 145,
                      },
                      height: {
                        xs: 105,
                        md: 145,
                      },
                      background: "#e9e4dc",
                      color: "#977743",
                      border: "5px solid #f7f4ef",
                      boxShadow: "0 10px 30px rgba(23,23,23,0.10)",
                    }}
                  >
                    {!userData?.profilePic && (
                      <PersonOutlineRoundedIcon
                        sx={{
                          fontSize: 48,
                        }}
                      />
                    )}
                  </Avatar>
                </Box>

                <Box
                  sx={{
                    minWidth: 0,
                    textAlign: {
                      xs: "center",
                      md: "left",
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{
                      xs: "center",
                      md: "flex-start",
                    }}
                    spacing={1}
                  >
                    <Typography
                      component="h1"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: {
                          xs: 34,
                          sm: 40,
                          md: 48,
                        },
                        fontWeight: 400,
                        lineHeight: 1.05,
                        letterSpacing: "-0.035em",
                        textTransform: "capitalize",
                      }}
                    >
                      {ownerName}
                    </Typography>

                    <VerifiedIcon
                      sx={{
                        color: "#977743",
                        fontSize: {
                          xs: 21,
                          md: 25,
                        },
                      }}
                    />
                  </Stack>

                  <Typography
                    sx={{
                      color: "#8b847c",
                      fontSize: 12,
                      mt: 0.8,
                    }}
                  >
                    @{username}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 2,
                      color: "#77716a",
                      fontSize: 12,
                      lineHeight: 1.7,
                    }}
                  >
                    Curated pieces available for your next occasion.
                  </Typography>

                  <Stack
                    direction="row"
                    justifyContent={{
                      xs: "center",
                      md: "flex-start",
                    }}
                    spacing={0}
                    sx={{
                      mt: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        pr: 3,
                        mr: 3,
                        borderRight: "1px solid #ddd7ce",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 19,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {followers}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.7,
                          fontSize: 9,
                          fontWeight: 600,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "#77716a",
                        }}
                      >
                        Followers
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 19,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {following}
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.7,
                          fontSize: 9,
                          fontWeight: 600,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "#77716a",
                        }}
                      >
                        Following
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: {
                      xs: "center",
                      md: "flex-end",
                    },
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    onClick={handleFollowToggle}
                    variant="contained"
                    sx={{
                      minWidth: 145,
                      height: 48,
                      borderRadius: 0,
                      background: isFollowing ? "#f1ede7" : "#171717",
                      color: isFollowing ? "#171717" : "#fff",
                      border: isFollowing
                        ? "1px solid #d7d0c6"
                        : "1px solid #171717",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      boxShadow: "none",
                      "&:hover": {
                        background: "#977743",
                        color: "#fff",
                        borderColor: "#977743",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isFollowing ? "Following" : "Follow"}
                  </Button>

                  <IconButton
                    onClick={() => setShareOpen(true)}
                    aria-label="Share profile"
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 0,
                      border: "1px solid #d7d0c6",
                      color: "#171717",
                      background: "#fff",
                      "&:hover": {
                        background: "#977743",
                        color: "#fff",
                        borderColor: "#977743",
                      },
                    }}
                  >
                    <ShareOutlinedIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: {
                    xs: 7,
                    md: 10,
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 1,
                      background: "#977743",
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: "#977743",
                    }}
                  >
                    Wardrobe Collection
                  </Typography>
                </Stack>

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  alignItems={{
                    xs: "flex-start",
                    sm: "flex-end",
                  }}
                  spacing={2}
                  sx={{
                    mb: 4,
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: {
                        xs: 34,
                        md: 48,
                      },
                      fontWeight: 400,
                      lineHeight: 1.05,
                      letterSpacing: "-0.035em",
                    }}
                  >
                    Rented Clothes
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#77716a",
                    }}
                  >
                    {productList.length}{" "}
                    {productList.length === 1 ? "piece" : "pieces"} available
                  </Typography>
                </Stack>

                {productList.length === 0 ? (
                  <Box
                    sx={{
                      background: "#fff",
                      border: "1px solid #ddd7ce",
                      py: {
                        xs: 6,
                        md: 8,
                      },
                      px: 3,
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f1ede7",
                      }}
                    >
                      <Inventory2OutlinedIcon
                        sx={{
                          color: "#977743",
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 28,
                        mb: 1,
                      }}
                    >
                      Nothing available here
                    </Typography>

                    <Typography
                      sx={{
                        maxWidth: 450,
                        mx: "auto",
                        color: "#77716a",
                        fontSize: 13,
                        lineHeight: 1.8,
                      }}
                    >
                      This wardrobe partner currently has no pieces available
                      for rental.
                    </Typography>

                    <Button
                      onClick={() => navigate("/")}
                      sx={{
                        mt: 3,
                        minHeight: 48,
                        px: 3,
                        borderRadius: 0,
                        background: "#171717",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        "&:hover": {
                          background: "#977743",
                        },
                      }}
                    >
                      Explore Collections
                    </Button>
                  </Box>
                ) : (
                  <Grid
                    container
                    spacing={{
                      xs: 2,
                      sm: 2.5,
                      md: 3,
                    }}
                  >
                    {productList.map((item, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        key={item?.productId || index}
                      >
                        <ProductCard
                          item={item}
                          onClick={() => handleProductClick(item?.productId)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>

              <Box
                sx={{
                  mt: {
                    xs: 8,
                    md: 12,
                  },
                  borderTop: "1px solid #ddd7ce",
                  pt: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <FavoriteBorderRoundedIcon
                    sx={{
                      fontSize: 17,
                      color: "#977743",
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: 10,
                      color: "#77716a",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Follow this wardrobe to discover new pieces.
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: 15,
                    color: "#171717",
                  }}
                >
                  Rent · Wear · Return
                </Typography>
              </Box>
            </Box>
          </Container>
        )}
      </Box>

      <Footer />

      <CommonShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Share Profile"
        name={ownerName}
        description={profileDescription}
        image={userData?.profilePic || ""}
        location=""
        shareUrl={profileShareUrl}
      />
    </>
  );
}
